import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, qs } from '../api/client';
import { Bandeau, Carte, Squelette } from '../components/ui';
import { ajouteJours, dateFr, isoJour, JOURS, montant, moisLong } from '../lib/format';
import { T, langue } from '../i18n';
import type { Vehicule } from '../api/types';

interface Occ {
  raison: string; reservationId?: string; statut?: string;
  debut: string; fin: string; libereLe: string; reference?: string; detail: string;
  client?: string; telephone?: string;
  /** Payé, payé en partie, ou rien versé — avec les montants pour le dire en clair. */
  paiement?: 'paye' | 'partiel' | 'impaye';
  encaisseMinor?: number; totalMinor?: number;
}
interface Ligne {
  vehicule: { id: string; marque: string; modele: string; annee: number; immatriculation: string; categorie: string; statut: string };
  occupations: Occ[];
}
interface Reponse { debut: string; fin: string; jours: number; tamponPreparationMin: number; lignes: Ligne[] }

interface Trou {
  vehicleId: string; libelle: string; immatriculation: string;
  debut: string; fin: string; jours: number;
  valeurEstimeeMinor: number; avant: string; apres: string;
}
interface Trous { nombre: number; joursTotal: number; valeurTotaleMinor: number; trous: Trou[] }

interface Dispo {
  disponibles: { vehicle: Vehicule; tarifJourMinor: number }[];
  occupes: { vehicle: Vehicule; libereLe: string; detail: string }[];
  message: string;
}

const MS_J = 86400000;
const CLE_FILTRE = 'friends.planning.vehicules';
const CLE_COULEURS = 'friends.planning.couleurs';
const CLE_GRILLAGE = 'friends.planning.grillage';

/**
 * Les couches du planning.
 *
 * Trois seulement par defaut — louee, reservee, disponible : c'est la lecture
 * que le patron veut, et toute couleur en plus la brouille. Les trois autres
 * (retard, entretien, trou a vendre) s'allument a la demande, avec leur
 * couleur au choix.
 *
 * Une couche eteinte ne fait PAS disparaitre l'occupation : une voiture au
 * garage reste occupee. Elle reprend simplement une des trois couleurs de base
 * — sinon le planning mentirait, et c'est le genre de mensonge qui fait sortir
 * une voiture qui n'aurait pas du.
 */
interface Couche {
  cle: 'loue' | 'reserve' | 'dispo' | 'retard' | 'entretien' | 'trou';
  label: string;
  defaut: string;
  /** Les trois de base ne s'eteignent pas : elles SONT le planning. */
  base?: boolean;
  aide?: string;
}
const COUCHES_BASE: Omit<Couche, 'label' | 'aide'>[] = [
  { cle: 'loue', defaut: '#15803D', base: true },
  { cle: 'reserve', defaut: '#6D28D9', base: true },
  { cle: 'dispo', defaut: '#C62828', base: true },
  /* Le retard se pose SUR le fond disponible : au meme rouge, la barre
     disparaissait. Elle reste rouge, en plus sombre, avec son liseré blanc. */
  { cle: 'retard', defaut: '#7A1C1C' },
  { cle: 'entretien', defaut: '#8A929E' },
  { cle: 'trou', defaut: '#F2A33C' },
];
/** Fonction : les libellés suivent la langue active. */
const couches = (): Couche[] => COUCHES_BASE.map((c) => ({
  ...c, label: T.planning.couches[c.cle] ?? c.cle, aide: T.planning.aides[c.cle],
}));

type Reglage = Record<string, { actif: boolean; couleur: string }>;
const REGLAGE_DEFAUT: Reglage = Object.fromEntries(
  COUCHES_BASE.map((c) => [c.cle, { actif: Boolean(c.base), couleur: c.defaut }]),
);

/** Le 1er du mois de la date donnee, a minuit. */
const premierDuMois = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * Le mois affiche se lit dans l'adresse : /planning?mois=2026-07. Sans lui, un
 * planning ne se partage pas — on ne peut pas envoyer « regarde juillet », on
 * doit dire « ouvre le planning puis clique deux fois sur la fleche ».
 * Adresse absente ou illisible : on retombe sur le mois en cours.
 */
function moisDeLAdresse(): Date {
  const brut = new URLSearchParams(window.location.search).get('mois');
  const m = brut?.match(/^(\d{4})-(\d{2})$/);
  if (!m) return premierDuMois(new Date());
  const mois = Number(m[2]);
  if (mois < 1 || mois > 12) return premierDuMois(new Date());
  return new Date(Number(m[1]), mois - 1, 1);
}
/** Nombre de jours entre le 1er du mois de `d` et la fin du n-ieme mois suivant. */
const joursSurMois = (d: Date, nbMois: number) =>
  Math.round((new Date(d.getFullYear(), d.getMonth() + nbMois, 1).getTime() - premierDuMois(d).getTime()) / MS_J);

/**
 * Le contenu d'une barre, sur trois lignes.
 *
 * Une barre qui ne portait que « 2026-0059 — Lila Benkhelifa » obligeait a
 * remonter la ligne pour savoir de quelle voiture il s'agit, et a survoler
 * pour connaitre les dates. Les trois informations tiennent dans la barre :
 * la plaque, le dossier et le client, puis la periode.
 */
function Trois({ l, o }: { l: Ligne; o: Occ }) {
  return (
    <span className="pl-txt">
      <b className="pl-txt-1">{l.vehicule.immatriculation}</b>
      <span className="pl-txt-2">{o.detail}</span>
      <span className="pl-txt-3">
        {dateFr(o.debut)} → {dateFr(o.fin)}
        {/* Le téléphone, à même la barre : la question qui suit « qui a cette
            voiture ? » est toujours « comment je le joins ? ». */}
        {o.telephone && (
          <a href={`tel:${o.telephone.replace(/\s+/g, '')}`} className="pl-tel"
             onClick={(e) => e.stopPropagation()} title={T.planning.appeler(o.client)}>
            📞 {o.telephone}
          </a>
        )}
      </span>
      {/*
        * Payé ou pas, sur la barre.
        * « Dans le planning, ajouter si payé partiellement ou pas du tout. »
        * Le montant versé sur le total évite de rouvrir le dossier pour savoir
        * s'il faut réclamer quelque chose au départ.
        */}
      {o.paiement && (
        <span className={`pl-paye pl-paye-${o.paiement}`}>
          {o.paiement === 'paye' ? T.planning.paye
            : o.paiement === 'impaye' ? T.planning.impaye
              : `${montant(o.encaisseMinor ?? 0)} / ${montant(o.totalMinor ?? 0)}`}
        </span>
      )}
    </span>
  );
}

export default function Planning() {
  /*
   * Le patron raisonne en mois, du 1er au 31 — c'est comme ca qu'il lit ses
   * chiffres et son planning. La periode part donc toujours du 1er, et se
   * compte en mois entiers, pas en « 30 jours glissants ».
   */
  const [ancre, setAncre] = useState(moisDeLAdresse);
  const [nbMois, setNbMois] = useState(1);
  const [d, setD] = useState<Reponse | null>(null);
  const [trous, setTrous] = useState<Trous | null>(null);
  const [survol, setSurvol] = useState<{ o: Occ; v: string } | null>(null);

  /* --- Choix des voitures affichees, conserve d'une visite a l'autre --- */
  const [tousVehicules, setTousVehicules] = useState<Vehicule[]>([]);
  const [choisis, setChoisis] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(CLE_FILTRE) ?? '[]') as string[]; } catch { return []; }
  });
  const [filtreOuvert, setFiltreOuvert] = useState(false);

  /* --- Couleurs, choisies une fois et conservees --- */
  const [couleurs, setCouleurs] = useState<Reglage>(() => {
    try {
      const brut = JSON.parse(localStorage.getItem(CLE_COULEURS) ?? 'null') as Reglage | null;
      return brut ? { ...REGLAGE_DEFAUT, ...brut } : REGLAGE_DEFAUT;
    } catch { return REGLAGE_DEFAUT; }
  });
  const [couleursOuvert, setCouleursOuvert] = useState(false);
  /* Le quadrillage des jours aide a compter, mais il hache les grandes plages
   * de couleur. On le coupe d'un bouton, et le choix se retient. */
  const [grillage, setGrillage] = useState(() => localStorage.getItem(CLE_GRILLAGE) !== 'non');
  useEffect(() => { localStorage.setItem(CLE_GRILLAGE, grillage ? 'oui' : 'non'); }, [grillage]);
  useEffect(() => { localStorage.setItem(CLE_COULEURS, JSON.stringify(couleurs)); }, [couleurs]);

  /* --- Selection au glisser : deux dates, et la reponse --- */
  const piste = useRef<HTMLDivElement | null>(null);
  const [glisse, setGlisse] = useState<{ a: number; b: number } | null>(null);
  const [selection, setSelection] = useState<{ debut: Date; fin: Date } | null>(null);
  const [dispo, setDispo] = useState<Dispo | null>(null);

  const jours = joursSurMois(ancre, nbMois);

  useEffect(() => {
    localStorage.setItem(CLE_FILTRE, JSON.stringify(choisis));
  }, [choisis]);

  useEffect(() => { api.get<Vehicule[]>('/vehicules').then(setTousVehicules).catch(() => {}); }, []);

  useEffect(() => {
    setD(null);
    api.get<Reponse>(`/disponibilites/planning${qs({
      debut: isoJour(ancre), jours, vehicules: choisis.length ? choisis.join(',') : undefined,
    })}`).then(setD).catch(() => {});
    api.get<Trous>(`/disponibilites/trous${qs({ debut: isoJour(ancre), jours })}`)
      .then(setTrous).catch(() => setTrous(null));
  }, [ancre, jours, choisis]);

  const colonnes = useMemo(
    () => Array.from({ length: jours }, (_, i) => ajouteJours(ancre, i)),
    [ancre, jours],
  );
  const auj = new Date().setHours(0, 0, 0, 0);

  /* --- Glisser sur une piste : on lit la fraction horizontale --- */
  const dateDeFraction = useCallback((f: number) => {
    const i = Math.max(0, Math.min(jours - 1, Math.floor(f * jours)));
    return ajouteJours(ancre, i);
  }, [ancre, jours]);

  const fractionDeSouris = (e: { clientX: number }, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
  };

  /**
   * Les ecouteurs sont poses DANS le mousedown, pas dans un effet.
   *
   * Passer par un useEffect fait perdre les premiers mouvements : le temps que
   * React re-rende et attache l'ecouteur, la souris a deja bouge. Un humain ne
   * le voit pas — le rendu prend 16 ms — mais un glisser rapide, ou un doigt sur
   * un pave tactile, perd le debut du geste et la selection se reduit a un jour.
   */
  function demarrerGlisse(e: React.MouseEvent<HTMLDivElement>) {
    // Un clic sur une barre ouvre le dossier : on ne demarre une selection que
    // sur le fond de la piste.
    if ((e.target as HTMLElement).closest('.pl-barre')) return;
    const el = e.currentTarget;
    piste.current = el;
    const depart = fractionDeSouris(e, el);
    let arrivee = depart;
    setGlisse({ a: depart, b: depart });
    setSelection(null); setDispo(null);

    const bouge = (ev: MouseEvent) => {
      arrivee = fractionDeSouris(ev, el);
      setGlisse({ a: depart, b: arrivee });
    };
    const relache = () => {
      window.removeEventListener('mousemove', bouge);
      window.removeEventListener('mouseup', relache);
      setGlisse(null);
      const [f1, f2] = depart <= arrivee ? [depart, arrivee] : [arrivee, depart];
      // La fin est INCLUSIVE : glisser sur une seule case veut dire « ce jour-la ».
      setSelection({ debut: dateDeFraction(f1), fin: ajouteJours(dateDeFraction(f2), 1) });
    };
    window.addEventListener('mousemove', bouge);
    window.addEventListener('mouseup', relache);
  }

  useEffect(() => {
    if (!selection) { setDispo(null); return; }
    setDispo(null);
    api.get<Dispo>(`/disponibilites${qs({ debut: isoJour(selection.debut), fin: isoJour(selection.fin) })}`)
      .then(setDispo).catch(() => {});
  }, [selection]);

  if (!d) return <Squelette lignes={8} />;

  const pos = (debutIso: string, finIso: string) => {
    const t0 = ancre.getTime();
    const total = jours * MS_J;
    const a = Math.max(t0, new Date(debutIso).getTime());
    const b = Math.min(t0 + total, new Date(finIso).getTime());
    if (b <= a) return null;
    return { left: `${((a - t0) / total) * 100}%`, width: `${Math.max(0.6, ((b - a) / total) * 100)}%` };
  };

  /*
   * Trois couleurs, et rien d'autre — c'est la demande du patron, et c'est la
   * bonne : vert la voiture travaille, violet elle est promise, rouge elle ne
   * rapporte rien. L'entretien n'est pas un de ces trois etats : il reste en
   * hachures grises pour ne pas fausser la lecture.
   */
  const couleur = (o: Occ): string => {
    // Un entretien occupe la voiture : si la couche est eteinte, il prend la
    // couleur « reservee » plutot que de disparaitre.
    if (o.raison !== 'reservation') return couleurs.entretien?.actif ? 'entretien' : 'reserve';
    const enRetard = new Date(o.fin) < new Date() && o.statut !== 'terminee';
    const loue = o.statut === 'en_cours' || o.statut === 'non_restitue' || o.statut === 'terminee';
    if (enRetard) return couleurs.retard?.actif ? 'retard' : (loue ? 'loue' : 'reserve');
    return loue ? 'loue' : 'reserve';
  };

  const trousParVehicule = new Map<string, Trou[]>();
  for (const t of trous?.trous ?? []) {
    if (!trousParVehicule.has(t.vehicleId)) trousParVehicule.set(t.vehicleId, []);
    trousParVehicule.get(t.vehicleId)!.push(t);
  }

  // Au-dela de deux mois, un numero par jour devient illisible : on bascule sur
  // des reperes de mois.
  const denses = jours > 62;
  const moisAffiches = Array.from({ length: nbMois }, (_, i) => {
    const m = new Date(ancre.getFullYear(), ancre.getMonth() + i, 1);
    const debut = Math.round((m.getTime() - ancre.getTime()) / MS_J);
    const fin = Math.round((new Date(m.getFullYear(), m.getMonth() + 1, 1).getTime() - ancre.getTime()) / MS_J);
    return { m, debut, largeur: fin - debut };
  });

  const L = T.planning;
  const COUCHES = couches();
  const titreMois = moisLong(ancre);
  const dernierMois = moisLong(new Date(ancre.getFullYear(), ancre.getMonth() + nbMois - 1, 1));

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{L.titre}</h1>
          <p>
            {L.vehicules(d.lignes.length)}{' '}
            {nbMois === 1 ? titreMois : L.deA(titreMois, dernierMois)}
            {choisis.length > 0 && L.selectionnees(choisis.length)}
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-s" onClick={() => setAncre(new Date(ancre.getFullYear(), ancre.getMonth() - nbMois, 1))}>‹</button>
          <button className="btn btn-s" onClick={() => setAncre(premierDuMois(new Date()))}>{L.ceMois}</button>
          <button className="btn btn-s" onClick={() => setAncre(new Date(ancre.getFullYear(), ancre.getMonth() + nbMois, 1))}>›</button>
          <select className="btn btn-s" value={nbMois} onChange={(e) => setNbMois(Number(e.target.value))} style={{ paddingRight: 8 }}>
            <option value={1}>{L.mois(1)}</option><option value={2}>{L.mois(2)}</option>
            <option value={3}>{L.mois(3)}</option><option value={6}>{L.mois(6)}</option>
            <option value={12}>{L.mois(12)}</option>
          </select>
          <button className={`btn btn-s ${choisis.length ? 'btn-p' : ''}`} onClick={() => setFiltreOuvert(!filtreOuvert)}>
            {choisis.length ? L.nVoitures(choisis.length) : L.choisirVoitures}
          </button>
          <button className={`btn btn-s ${grillage ? '' : 'btn-p'}`}
                  onClick={() => setGrillage(!grillage)}
                  title={grillage ? L.masquerQuadrillage : L.afficherQuadrillage}>
            {grillage ? L.masquerGrillage : L.afficherGrillage}
          </button>
          <button className="btn btn-s" onClick={() => setCouleursOuvert(!couleursOuvert)}>{L.couleurs}</button>
        </div>
      </div>

      {filtreOuvert && (
        <Carte titre={L.voituresAffichees} action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-s" onClick={() => setChoisis([])}>{L.toutes}</button>
            <button className="btn btn-s" onClick={() => setFiltreOuvert(false)}>{L.fermer}</button>
          </div>
        }>
          <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 10 }}>
            {L.aucuneCoche}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 }}>
            {tousVehicules.map((v) => (
              <label key={v._id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={choisis.includes(v._id)}
                       onChange={(e) => setChoisis(e.target.checked
                         ? [...choisis, v._id]
                         : choisis.filter((x) => x !== v._id))} />
                <span>{v.marque} {v.modele} <span className="mini sourdine">{v.immatriculation}</span></span>
              </label>
            ))}
          </div>
        </Carte>
      )}

      {couleursOuvert && (
        <Carte titre={L.couleursPlanning} action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-s" onClick={() => setCouleurs(REGLAGE_DEFAUT)}>{L.retablir}</button>
            <button className="btn btn-s" onClick={() => setCouleursOuvert(false)}>{L.fermer}</button>
          </div>
        }>
          <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 12 }}>
            {L.couleursExplication}
          </p>
          <div className="couleurs">
            {COUCHES.map((c) => (
              <label key={c.cle} className="couleur-ligne">
                <input type="color" value={couleurs[c.cle]?.couleur ?? c.defaut}
                       aria-label={L.couleurDe(c.label)}
                       onChange={(e) => setCouleurs({
                         ...couleurs,
                         [c.cle]: { actif: couleurs[c.cle]?.actif ?? Boolean(c.base), couleur: e.target.value },
                       })} />
                <span className="nom">
                  {c.label}
                  {c.aide && <div className="fixe">{c.aide}</div>}
                </span>
                {c.base ? (
                  <span className="fixe">{L.toujours}</span>
                ) : (
                  <input type="checkbox" checked={couleurs[c.cle]?.actif ?? false}
                         aria-label={L.afficherCouche(c.label)}
                         onChange={(e) => setCouleurs({
                           ...couleurs,
                           [c.cle]: { actif: e.target.checked, couleur: couleurs[c.cle]?.couleur ?? c.defaut },
                         })} />
                )}
              </label>
            ))}
          </div>
        </Carte>
      )}

      {/*
        * « L'argent qui dort » : une voiture qui rentre mardi et repart samedi a
        * trois jours creux au milieu. Ces journees-la sont les plus faciles a
        * vendre — la voiture est deja la, deja preparee — et personne ne les
        * voyait, parce qu'elles ne sont ni une location ni une voiture libre.
        */}
      {couleurs.trou?.actif && trous && trous.nombre > 0 && (
        <div style={{ margin: '10px 0 14px' }}>
          <Bandeau type="warn" titre={L.trousTitre(trous.nombre, montant(trous.valeurTotaleMinor))}>
            {L.trousA(trous.joursTotal)}<b>{L.entreDeux}</b>{L.trousB}
          </Bandeau>
        </div>
      )}

      <div className="legende" style={{ marginBottom: 10 }}>
        {COUCHES.filter((c) => couleurs[c.cle]?.actif).map((c) => (
          <span key={c.cle}>
            <i style={{ background: couleurs[c.cle]!.couleur, border: '1px solid rgba(0,0,0,.12)' }} />
            {c.label}
          </span>
        ))}
      </div>

      <div className={`planning ${grillage ? '' : 'sans-grillage'}`} style={Object.fromEntries(
        COUCHES.map((c) => [`--pl-${c.cle}`, couleurs[c.cle]?.couleur ?? c.defaut]),
      ) as React.CSSProperties}>
        <div className="pl-grille">
          <div className="pl-tete">
            <div className="pl-veh"><b>{L.vehicule}</b></div>
            {denses ? (
              <div className="pl-jours" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                {moisAffiches.map((x, i) => (
                  <div key={i} className="pl-jour pl-mois"
                       style={{ gridColumn: `${x.debut + 1} / span ${x.largeur}` }}>
                    <b>{x.m.toLocaleDateString(langue === 'fr' ? 'fr-FR' : 'en-GB', { month: 'short', year: '2-digit' })}</b>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pl-jours" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                {colonnes.map((c, i) => {
                  const j = new Date(c);
                  const we = j.getDay() === 0 || j.getDay() === 6;
                  const estAuj = new Date(c).setHours(0, 0, 0, 0) === auj;
                  return (
                    <div key={i} className={`pl-jour ${we ? 'we' : ''} ${estAuj ? 'auj' : ''}`}>
                      {JOURS[j.getDay()]}<b>{j.getDate()}</b>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {d.lignes.map((l) => (
            <div className="pl-ligne" key={l.vehicule.id}>
              <div className="pl-veh">
                <b>{l.vehicule.marque} {l.vehicule.modele}</b>
                <span>{l.vehicule.immatriculation} · {l.vehicule.categorie}</span>
              </div>
              <div className="pl-piste" onMouseDown={demarrerGlisse}>
                <div className="pl-fond" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                  {colonnes.map((c, i) => {
                    const j = new Date(c);
                    const we = j.getDay() === 0 || j.getDay() === 6;
                    const estAuj = new Date(c).setHours(0, 0, 0, 0) === auj;
                    return <div key={i} className={`pl-cell ${we ? 'we' : ''} ${estAuj ? 'auj' : ''}`} />;
                  })}
                </div>

                {(couleurs.trou?.actif ? trousParVehicule.get(l.vehicule.id) ?? [] : []).map((t, i) => {
                  const p = pos(t.debut, t.fin);
                  return p && (
                    <div key={`t${i}`} className="pl-trou" style={p}
                         title={L.trouTitre(t.jours, montant(t.valeurEstimeeMinor))} />
                  );
                })}

                {l.occupations.map((o, i) => {
                  const p = pos(o.debut, o.fin);
                  const tampon = pos(o.fin, o.libereLe);
                  const classe = couleur(o);
                  return (
                    <span key={i}>
                      {p && (o.reservationId ? (
                        <Link className={`pl-barre ${classe}`} style={p} to={`/reservations/${o.reservationId}`}
                              title={L.ouvrirDossier(o.reference ?? '')}
                              onMouseEnter={() => setSurvol({ o, v: l.vehicule.immatriculation })}
                              onMouseLeave={() => setSurvol(null)}>
                          {denses ? '' : <Trois l={l} o={o} />}
                        </Link>
                      ) : (
                        <div className={`pl-barre ${classe}`} style={p}
                             onMouseEnter={() => setSurvol({ o, v: l.vehicule.immatriculation })}
                             onMouseLeave={() => setSurvol(null)}>
                          {denses ? '' : <Trois l={l} o={o} />}
                        </div>
                      ))}
                      {tampon && o.raison === 'reservation' && (
                        <div className="pl-barre tampon" style={tampon}
                             title={L.preparation(d.tamponPreparationMin)} />
                      )}
                    </span>
                  );
                })}

                {/*
                  * Le quadrillage passe AU-DESSUS des barres.
                  *
                  * Dessine en fond, il ne separait que les journees libres : sur une
                  * location de huit jours, la barre couvrait tout et on ne pouvait plus
                  * compter les jours. Ici il traverse tout, y compris le vert et le
                  * violet, et il ne prend aucun clic — le glisser et l'ouverture du
                  * dossier passent au travers.
                  */}
                {grillage && (
                  <div className="pl-quadrillage" aria-hidden="true"
                       style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                    {colonnes.map((_, i) => <span key={i} />)}
                  </div>
                )}

                {glisse && (
                  <div className="pl-selection" style={{
                    left: `${Math.min(glisse.a, glisse.b) * 100}%`,
                    width: `${Math.abs(glisse.b - glisse.a) * 100}%`,
                  }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Glisser sur le planning repond a la seule question du comptoir :
          « qu'est-ce qui est libre sur ces dates ? » */}
      {selection && (
        <div style={{ marginTop: 14 }}>
          <Carte titre={L.duAu(dateFr(selection.debut), dateFr(ajouteJours(selection.fin, -1)))}
                 action={<button className="btn btn-s" onClick={() => { setSelection(null); setDispo(null); }}>{L.effacer}</button>}>
            {dispo === null ? <Squelette lignes={2} /> : (
              <>
                <p style={{ marginTop: -4 }}>{dispo.message}</p>
                {dispo.disponibles.length > 0 && (
                  <>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {dispo.disponibles.map((x) => (
                        <Link key={x.vehicle._id} className="btn btn-s"
                              to={`/reservations/nouvelle?vehicle=${x.vehicle._id}&debut=${isoJour(selection.debut)}&fin=${isoJour(selection.fin)}`}>
                          {x.vehicle.marque} {x.vehicle.modele} · {L.parJour(montant(x.tarifJourMinor))}
                        </Link>
                      ))}
                    </div>
                    <p className="mini sourdine" style={{ marginTop: 10 }}>
                      {L.cliquezVoiture}
                    </p>
                  </>
                )}
              </>
            )}
          </Carte>
        </div>
      )}

      {survol && (
        <div style={{ position: 'fixed', bottom: 20, left: 280, zIndex: 20 }}>
          <Carte>
            <b className="num">{survol.v}</b>
            <div className="mini sourdine">{survol.o.detail}</div>
            <div className="mini">{dateFr(survol.o.debut)} → {dateFr(survol.o.fin)}{L.libreLe(dateFr(survol.o.libereLe))}</div>
          </Carte>
        </div>
      )}

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        <b>{L.glissez}</b>{L.aideBas}

      </p>
    </>
  );
}
